import React, { useCallback, useEffect, useRef, useState } from 'react';

import '../../global.css';

import {
  aguardarFontes,
  gerarCard,
  linkDoImovel,
  nomeDoArquivo,
  textoCompartilhar,
} from './card';
import { Aviso, BarraAcoes, Botao, Cartao, Container, Grade, Lupa, Titulo } from './styles';

// Pagina publica: o corretor chega pelo Linktree ou pelo boletim, sempre
// deslogado. Por isso a busca e um fetch direto no endpoint publico, e nao o
// property.service, que trata erro redirecionando para login.
const buscarCatalogo = async () => {
  const resposta = await fetch(`${process.env.REACT_APP_API_URL}/property?limit=9999&sold=false`);
  if (!resposta.ok) throw new Error('não foi possível carregar o catálogo');
  const dados = await resposta.json();
  return (dados.propertys || [])
    .filter((p) => String(p.showCatalog) !== 'false' && p.arquive !== true)
    .sort((a, b) => {
      const bairro = (a.neighborhoodName || '').localeCompare(b.neighborhoodName || '', 'pt-BR');
      return bairro !== 0 ? bairro : (a.address || '').localeCompare(b.address || '', 'pt-BR');
    });
};

const baixarBlob = (blob, nome) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// No celular o compartilhamento nativo manda a IMAGEM direto pro WhatsApp, que
// e o uso real. Onde isso nao existe (a maioria dos desktops), cai para copiar
// o texto com o link do imovel.
const compartilhar = async (blob, imovel) => {
  const arquivo = new File([blob], nomeDoArquivo(imovel), { type: 'image/jpeg' });
  const texto = textoCompartilhar(imovel);

  try {
    if (navigator.canShare && navigator.canShare({ files: [arquivo] })) {
      await navigator.share({ files: [arquivo], text: texto });
      return null;
    }
    if (navigator.share) {
      await navigator.share({ text: texto, url: linkDoImovel(imovel) });
      return null;
    }
  } catch (e) {
    // o usuario cancelar a folha de compartilhamento cai aqui; nao e erro
    if (e && e.name === 'AbortError') return null;
  }

  try {
    await navigator.clipboard.writeText(texto);
    return 'Link copiado';
  } catch (e) {
    return 'Não foi possível compartilhar';
  }
};

export default function Cards() {
  const [imoveis, setImoveis] = useState([]);
  const [estados, setEstados] = useState({}); // id -> { situacao, blob, url }
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [baixandoTodos, setBaixandoTodos] = useState(false);
  const [ampliado, setAmpliado] = useState(null);
  const [recado, setRecado] = useState(null);

  const montado = useRef(true);
  const urlsCriados = useRef([]);
  const emAndamento = useRef(new Set());

  // Gera sob demanda. Cada card puxa a foto em tamanho cheio; gerar os 31 de
  // uma vez seriam dezenas de MB — inviavel para o corretor no celular, que e
  // justamente quem mais usa.
  const gerar = useCallback(async (imovel) => {
    const id = imovel._id;
    if (emAndamento.current.has(id)) return null;
    emAndamento.current.add(id);
    setEstados((a) => (a[id] ? a : { ...a, [id]: { situacao: 'gerando' } }));

    try {
      const blob = await gerarCard(imovel);
      if (!montado.current) return null;
      const url = URL.createObjectURL(blob);
      urlsCriados.current.push(url);
      setEstados((a) => ({ ...a, [id]: { situacao: 'pronto', blob, url } }));
      return blob;
    } catch (e) {
      if (montado.current) setEstados((a) => ({ ...a, [id]: { situacao: 'erro' } }));
      return null;
    } finally {
      emAndamento.current.delete(id);
    }
  }, []);

  useEffect(() => {
    montado.current = true;

    (async () => {
      try {
        const [lista] = await Promise.all([buscarCatalogo(), aguardarFontes()]);
        if (!montado.current) return;
        setImoveis(lista);
      } catch (e) {
        if (montado.current) setErro(e.message);
      } finally {
        if (montado.current) setCarregando(false);
      }
    })();

    return () => {
      montado.current = false;
      urlsCriados.current.forEach((url) => URL.revokeObjectURL(url));
      urlsCriados.current = [];
    };
  }, []);

  // Esc fecha a lupa, e a pagina atras nao rola enquanto ela esta aberta
  useEffect(() => {
    if (!ampliado) return undefined;
    const aoTeclar = (e) => e.key === 'Escape' && setAmpliado(null);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', aoTeclar);
    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [ampliado]);

  useEffect(() => {
    if (!recado) return undefined;
    const t = setTimeout(() => setRecado(null), 2500);
    return () => clearTimeout(t);
  }, [recado]);

  const aoCompartilhar = useCallback(async (blob, imovel) => {
    const resposta = await compartilhar(blob, imovel);
    if (resposta && montado.current) setRecado(resposta);
  }, []);

  const baixarTodos = useCallback(async () => {
    setBaixandoTodos(true);
    for (const imovel of imoveis) {
      if (!montado.current) break;
      const pronto = estados[imovel._id];
      const blob = pronto && pronto.blob ? pronto.blob : await gerar(imovel);
      if (!blob) continue;
      baixarBlob(blob, nomeDoArquivo(imovel));
      // o navegador engasga se os downloads saem todos no mesmo instante
      await new Promise((r) => setTimeout(r, 400));
    }
    if (montado.current) setBaixandoTodos(false);
  }, [imoveis, estados, gerar]);

  const prontos = imoveis.filter((i) => estados[i._id] && estados[i._id].situacao === 'pronto');
  const estadoAmpliado = ampliado ? estados[ampliado._id] : null;

  return (
    <>
      <Container>
        <Titulo>
          <h1>Cards de divulgação</h1>
          <p>Imagens prontas para você divulgar os imóveis no WhatsApp e nas redes sociais.</p>
        </Titulo>

        <Aviso>
          Os valores impressos no card são os do catálogo neste momento. Baixe na hora de divulgar
          para não mandar preço vencido ao cliente.
        </Aviso>

        <BarraAcoes>
          <span>
            {carregando
              ? 'Carregando o catálogo…'
              : `${imoveis.length} ${imoveis.length === 1 ? 'imóvel' : 'imóveis'} à venda`}
          </span>
          <Botao onClick={baixarTodos} disabled={!imoveis.length || baixandoTodos}>
            {baixandoTodos ? 'Baixando…' : 'Baixar todos'}
          </Botao>
        </BarraAcoes>

        {erro && <p>Não foi possível carregar os cards: {erro}</p>}

        <Grade>
          {imoveis.map((imovel) => (
            <CardDoImovel
              key={imovel._id}
              imovel={imovel}
              estado={estados[imovel._id]}
              aoAparecer={gerar}
              aoAmpliar={setAmpliado}
              aoCompartilhar={aoCompartilhar}
            />
          ))}
        </Grade>

        {!carregando && !!imoveis.length && (
          <p style={{ color: '#a0a0a0', fontSize: 13, marginTop: 24 }}>
            {prontos.length} de {imoveis.length} cards gerados.
          </p>
        )}
      </Container>

      {ampliado && estadoAmpliado && estadoAmpliado.situacao === 'pronto' && (
        <Lupa onClick={() => setAmpliado(null)}>
          <button className="fechar" onClick={() => setAmpliado(null)} aria-label="Fechar">
            ×
          </button>
          <img
            src={estadoAmpliado.url}
            alt={`${ampliado.neighborhoodName} — ${ampliado.address}`}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="acoes" onClick={(e) => e.stopPropagation()}>
            <Botao onClick={() => aoCompartilhar(estadoAmpliado.blob, ampliado)}>
              Compartilhar
            </Botao>
            <Botao
              $secundario
              onClick={() => baixarBlob(estadoAmpliado.blob, nomeDoArquivo(ampliado))}
            >
              Baixar
            </Botao>
          </div>
        </Lupa>
      )}

      {recado && (
        <div
          style={{
            background: '#333',
            borderRadius: 4,
            bottom: 24,
            color: '#fff',
            fontSize: 14,
            left: '50%',
            padding: '10px 18px',
            position: 'fixed',
            transform: 'translateX(-50%)',
            zIndex: 1100,
          }}
        >
          {recado}
        </div>
      )}
    </>
  );
}

function CardDoImovel({ imovel, estado, aoAparecer, aoAmpliar, aoCompartilhar }) {
  const ref = useRef(null);
  const jaPediu = useRef(false);

  useEffect(() => {
    const alvo = ref.current;
    if (!alvo) return undefined;

    // navegador sem IntersectionObserver: gera direto, sem preguica
    if (typeof IntersectionObserver === 'undefined') {
      if (!jaPediu.current) {
        jaPediu.current = true;
        aoAparecer(imovel);
      }
      return undefined;
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting && !jaPediu.current) {
            jaPediu.current = true;
            aoAparecer(imovel);
            observador.disconnect();
          }
        });
      },
      { rootMargin: '400px' } // comeca um pouco antes de entrar na tela
    );

    observador.observe(alvo);
    return () => observador.disconnect();
  }, [imovel, aoAparecer]);

  const situacao = (estado && estado.situacao) || 'espera';

  return (
    <Cartao ref={ref}>
      {situacao === 'pronto' ? (
        <img
          src={estado.url}
          alt={`${imovel.neighborhoodName} — ${imovel.address}`}
          onClick={() => aoAmpliar(imovel)}
        />
      ) : (
        <div className="placeholder">
          {situacao === 'erro' ? 'não foi possível gerar este card' : 'gerando…'}
        </div>
      )}
      <div className="rodape">
        {/* visualizador deste mesmo dominio — este site nao referencia a marca */}
        <a href={`/${imovel._id}`} target="_blank" rel="noopener noreferrer">
          ver o imóvel
        </a>
        <Botao
          $compacto
          disabled={situacao !== 'pronto'}
          onClick={() => aoCompartilhar(estado.blob, imovel)}
        >
          Compartilhar
        </Botao>
        <Botao
          $compacto
          $secundario
          disabled={situacao !== 'pronto'}
          onClick={() => baixarBlob(estado.blob, nomeDoArquivo(imovel))}
        >
          Baixar
        </Botao>
      </div>
    </Cartao>
  );
}
